<script setup lang="ts">
import { computed, ref } from 'vue'
import ResourcePickerModal from '@/features/virtual-resource/components/ResourcePickerModal.vue'
import {
  FONT_RESOURCE_BASE_FOLDER,
  resolveFontDisplayNameFromStoredPath,
} from '@/features/project-font/runtime'
import { resolveFullDisplayPathFromFileName } from '@/features/virtual-resource/fileNamePath'
import { useWorkspaceStore } from '@/store/workspace'

const props = withDefaults(
  defineProps<{
    modelValue: string
    disabled?: boolean
    pickerTitle?: string
    placeholder?: string
    baseFolderPath?: string
  }>(),
  {
    baseFolderPath: FONT_RESOURCE_BASE_FOLDER,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const workspace = useWorkspaceStore()
const pickerOpen = ref(false)
const pickerSearch = ref('')

const displayValue = computed(() => {
  const text = resolveFontDisplayNameFromStoredPath(
    workspace.virtualResourceRoots.font,
    props.modelValue ?? '',
    props.baseFolderPath,
  )
  return text || props.placeholder || '请选择字体'
})

const selectedPath = computed(() => {
  return String(props.modelValue || '').trim()
})

const fullDisplayPath = computed(() => {
  return resolveFullDisplayPathFromFileName(
    workspace.virtualResourceRoots.font,
    'font',
    props.modelValue ?? '',
    'relative',
    props.baseFolderPath,
  )
})

function openPicker() {
  if (props.disabled) return
  pickerSearch.value = ''
  pickerOpen.value = true
}
</script>

<template>
  <div
    class="font-select-field"
    :class="{
      'font-select-field--disabled': disabled,
    }"
  >
    <input
      class="font-select-input"
      type="text"
      :value="displayValue"
      :title="fullDisplayPath || displayValue"
      :placeholder="placeholder ?? '请选择字体'"
      :disabled="disabled"
      readonly
    />
    <button
      type="button"
      class="font-select-trigger"
      :disabled="disabled"
      :title="pickerTitle ?? '选择字体'"
      @click="openPicker"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M11.6 1.4a1.5 1.5 0 0 1 2.1 2.1l-7.3 7.3-3 0.8 0.8-3 7.4-7.2Zm1.4 2.1a.5.5 0 0 0-.7-.7L5.2 9.9l-0.4 1.4 1.4-0.4L13 3.5ZM2 13h12v1H2z"
          fill="currentColor"
        />
      </svg>
    </button>

    <ResourcePickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      category="font"
      :title="pickerTitle ?? '选择字体'"
      :search="pickerSearch"
      :selected-path="selectedPath"
      :base-folder-path="baseFolderPath"
      @update:search="pickerSearch = $event"
      @select="emit('update:modelValue', $event)"
    />
  </div>
</template>

<style scoped>
.font-select-field {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.font-select-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 4px 34px 4px 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  font-size: 12px;
  line-height: 20px;
  box-sizing: border-box;
}

.font-select-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.font-select-trigger {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 26px;
  height: calc(100% - 2px);
  padding: 0;
  border: 0;
  border-left: 1px solid var(--theme-border);
  background: var(--theme-bg-surface);
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.font-select-trigger:hover:not(:disabled) {
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
}

.font-select-trigger:disabled,
.font-select-input:disabled {
  cursor: not-allowed;
}

.font-select-trigger svg {
  width: 14px;
  height: 14px;
}

.font-select-field--disabled .font-select-input {
  opacity: 0.72;
}
</style>
